/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BearerResponse } from '../models/BearerResponse';
import type { Body_auth_auth_bearer_login } from '../models/Body_auth_auth_bearer_login';
import type { Body_auth_auth_cookie_login } from '../models/Body_auth_auth_cookie_login';
import type { Body_auth_verify_request_token } from '../models/Body_auth_verify_request_token';
import type { Body_auth_verify_verify } from '../models/Body_auth_verify_verify';
import type { CliExchangeRequest } from '../models/CliExchangeRequest';
import type { OAuth2AuthorizeResponse } from '../models/OAuth2AuthorizeResponse';
import type { User } from '../models/User';
import type { UserCreate } from '../models/UserCreate';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AuthService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Auth:Bearer.Login
     * @returns BearerResponse Successful Response
     * @throws ApiError
     */
    public authBearerLogin({
        formData,
    }: {
        formData: Body_auth_auth_bearer_login,
    }): CancelablePromise<BearerResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/bearer/login',
            formData: formData,
            mediaType: 'application/x-www-form-urlencoded',
            errors: {
                400: `Bad Request`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Auth:Bearer.Logout
     * @returns any Successful Response
     * @throws ApiError
     */
    public authBearerLogout(): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/bearer/logout',
            errors: {
                401: `Missing token or inactive user.`,
            },
        });
    }

    /**
     * Auth:Cookie.Login
     * @returns any Successful Response
     * @throws ApiError
     */
    public authCookieLogin({
        formData,
    }: {
        formData: Body_auth_auth_cookie_login,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/cookie/login',
            formData: formData,
            mediaType: 'application/x-www-form-urlencoded',
            errors: {
                400: `Bad Request`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Auth:Cookie.Logout
     * @returns any Successful Response
     * @throws ApiError
     */
    public authCookieLogout(): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/cookie/logout',
            errors: {
                401: `Missing token or inactive user.`,
            },
        });
    }

    /**
     * Register:Register
     * @returns User Successful Response
     * @throws ApiError
     */
    public registerRegister({
        requestBody,
    }: {
        requestBody: UserCreate,
    }): CancelablePromise<User> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/register',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Verify:Request-Token
     * @returns any Successful Response
     * @throws ApiError
     */
    public verifyRequestToken({
        requestBody,
    }: {
        requestBody: Body_auth_verify_request_token,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/request-verify-token',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Verify:Verify
     * @returns User Successful Response
     * @throws ApiError
     */
    public verifyVerify({
        requestBody,
    }: {
        requestBody: Body_auth_verify_verify,
    }): CancelablePromise<User> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/verify',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Exchange Cli Handoff Code
     * @returns any Successful Response
     * @throws ApiError
     */
    public exchangeCliHandoffCode({
        requestBody,
    }: {
        requestBody: CliExchangeRequest,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/cli/exchange',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Oauth Providers
     * @returns any Successful Response
     * @throws ApiError
     */
    public getOauthProviders(): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/providers',
        });
    }

    /**
     * Oauth:Azure.Cookie.Authorize
     * @returns OAuth2AuthorizeResponse Successful Response
     * @throws ApiError
     */
    public oauthCookieAuthorize({
        scopes,
    }: {
        scopes?: Array<string>,
    }): CancelablePromise<OAuth2AuthorizeResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/cookie/azure/authorize',
            query: {
                'scopes': scopes,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Oauth:Azure.Cookie.Callback
     * The response varies based on the authentication backend used.
     * @returns any Successful Response
     * @throws ApiError
     */
    public oauthCookieCallback({
        code,
        codeVerifier,
        state,
        error,
    }: {
        code?: string,
        codeVerifier?: string,
        state?: string,
        error?: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/cookie/azure/callback',
            query: {
                'code': code,
                'code_verifier': codeVerifier,
                'state': state,
                'error': error,
            },
            errors: {
                400: `Bad Request`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Oauth:Azure.Bearer.Authorize
     * @returns OAuth2AuthorizeResponse Successful Response
     * @throws ApiError
     */
    public oauthBearerAuthorize({
        scopes,
    }: {
        scopes?: Array<string>,
    }): CancelablePromise<OAuth2AuthorizeResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/bearer/azure/authorize',
            query: {
                'scopes': scopes,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Oauth:Azure.Bearer.Callback
     * The response varies based on the authentication backend used.
     * @returns any Successful Response
     * @throws ApiError
     */
    public oauthBearerCallback({
        code,
        codeVerifier,
        state,
        error,
    }: {
        code?: string,
        codeVerifier?: string,
        state?: string,
        error?: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/bearer/azure/callback',
            query: {
                'code': code,
                'code_verifier': codeVerifier,
                'state': state,
                'error': error,
            },
            errors: {
                400: `Bad Request`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Oauth:Azure.Cli Handoff.Authorize
     * @returns OAuth2AuthorizeResponse Successful Response
     * @throws ApiError
     */
    public oauthCliHandoffAuthorize({
        scopes,
    }: {
        scopes?: Array<string>,
    }): CancelablePromise<OAuth2AuthorizeResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/cli/azure/authorize',
            query: {
                'scopes': scopes,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Oauth:Azure.Cli Handoff.Callback
     * The response varies based on the authentication backend used.
     * @returns any Successful Response
     * @throws ApiError
     */
    public oauthCliHandoffCallback({
        code,
        codeVerifier,
        state,
        error,
    }: {
        code?: string,
        codeVerifier?: string,
        state?: string,
        error?: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/cli/azure/callback',
            query: {
                'code': code,
                'code_verifier': codeVerifier,
                'state': state,
                'error': error,
            },
            errors: {
                400: `Bad Request`,
                422: `Validation Error`,
            },
        });
    }

}
